import type { User as AuthUser } from "firebase/auth"
import { useCallback, useState } from "react"

import { getOauthStateParam } from "../auth/api"
import { getGoogleAccessToken } from "../auth/google"
import { GOOGLE_PROVIDER } from "../credentials/providerTypes"
import { isAxiosError } from "../errors"
import { doOauthFlow } from "./useUserCredential/doOauthFlow"

const loadAccessToken = async (
  requiredScopes: string[],
  credentialKind: "USER" | "GROUP",
): Promise<string | null> => {
  // Check if the access token is expired.
  try {
    const {
      data: { accessToken },
    } = await getGoogleAccessToken({
      credentialKind,
      requiredScopes,
    })
    return accessToken
  } catch (error) {
    if (
      isAxiosError(error) &&
      ((error.response?.data ?? {}) as { code?: number }).code === 404
    ) {
      return null
    }
    throw error
  }
}

interface UseGoogleAccessTokenResult {
  getAccessToken: (forceReconnect?: boolean) => Promise<string>
}

const useGoogleAccessToken = (
  authUser: AuthUser,
  requiredScopes: string[],
  credentialKind: "USER" | "GROUP" = "USER",
): UseGoogleAccessTokenResult => {
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(
    null,
  )

  const reconnectGoogle = useCallback(async () => {
    const { data } = await getOauthStateParam({
      credentialKind,
    })
    await doOauthFlow(GOOGLE_PROVIDER, data, {
      // space-delimited list of scopes
      scope: requiredScopes.join(" "),
      ...(authUser.email ? { login_hint: authUser.email } : {}),
    })
    const accessToken = await loadAccessToken(requiredScopes, credentialKind)
    if (accessToken) {
      setGoogleAccessToken(accessToken)
      return accessToken
    }
    throw new Error("Failed to get access token")
  }, [credentialKind, authUser.email, requiredScopes])

  const getAccessToken = useCallback(
    async (forceReconnect?: boolean) => {
      if (forceReconnect) {
        return reconnectGoogle()
      }

      if (googleAccessToken) {
        return googleAccessToken
      }

      // Try to get existing token.
      const accessToken = await loadAccessToken(requiredScopes, credentialKind)
      if (accessToken) {
        setGoogleAccessToken(accessToken)
        return accessToken
      }

      return reconnectGoogle()
    },
    [googleAccessToken, reconnectGoogle, requiredScopes, credentialKind],
  )

  return {
    getAccessToken,
  }
}

export default useGoogleAccessToken
